import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { RowData } from "../Interfaces/interfaces";
import {
  StatisticPanels,
  StatisticPanelsProps,
} from "../../../Components/StaticticsPanels/StatisticsPanel";
import { getCustomIDStatus } from "../Components/FilterBars/auditFilters";
import { getPrivacy } from "../Components/FilterBars/auditFilters";
import {
  buildQueryString,
  parseBooleanParam,
  parseStringArrayParam,
} from "../../../utils/urlFilters";
import MetadataTable from "../Components/Tables/metadataTable";
import { metadataExportRows } from "../Objects/exportDataObjects";
import AuditFilters from "../Components/FilterBars/auditFilters";
import { Skeleton } from "primereact/skeleton";
import {
  ChannelManagerOption,
  metadataFilterOptions,
} from "../Objects/multiSelectObjects";
import { useYouTubeScorecardData } from "../../../hooks/useYouTubeScorecardData";
import { useYouTubeScorecardFilters } from "../../../hooks/useYouTubeScorecardFilters";
import {
  isMissingCustomThumbnail,
  metadataFilterOptions as filterOptions,
} from "../Objects/filterOptions";
import LoadingState from "../Components/LoadingState";
import ErrorState from "../Components/ErrorState";
import { trackToolAction } from "../../../utils/analytics";

// Define the context type for the outlet
interface OutletContext {
  channelManagerOptionsForFilter: ChannelManagerOption[];
}

const MetadataTab: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get channel manager data from parent route context
  const outletCtx = useOutletContext<OutletContext | undefined>();
  const channelManagerOptionsForFilter =
    outletCtx?.channelManagerOptionsForFilter ?? ([] as ChannelManagerOption[]);

  // Debug log
  console.log("[MetadataTab] outletCtx:", outletCtx);
  console.log("[MetadataTab] channelManagerOptionsForFilter:", channelManagerOptionsForFilter);

  // Use custom hooks for data and filtering
  const {
    data: rows,
    loading,
    error,
    refreshData,
  } = useYouTubeScorecardData("metadata");
  const {
    filters,
    filteredRows,
    myIssuesRows,
    myIssuesOnly,
    handleFilterChange,
    applyFilters,
  } = useYouTubeScorecardFilters(rows);


  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      videoId: parseStringArrayParam(params, "videoId"),
      cms: parseStringArrayParam(params, "cms"),
      channel: parseStringArrayParam(params, "channel"),
      channelId: parseStringArrayParam(params, "channelId"),
      channelManager: parseStringArrayParam(params, "channelManager"),
      issue: parseStringArrayParam(params, "issue"),
      privacyStatus: parseStringArrayParam(params, "privacyStatus"),
      contentFormat: parseStringArrayParam(params, "contentFormat"),
      legalEntity: parseStringArrayParam(params, "legalEntity"),
      legalEntityType: parseStringArrayParam(params, "legalEntityType"),
      myIssuesOnly: parseBooleanParam(params, "myIssuesOnly"),
    };
  }, [location.search]);

  // Apply filters when rows or filters change
  useEffect(() => {
    applyFilters(filters, filterOptions);
  }, [rows, filters, applyFilters]);

  const updateUrlFromFilters = useCallback(
    (nextFilters: any) => {
      const isSelected = (value: any) =>
        value === true || value?.checked === true;
      const selectedChannelNames = Object.entries(
        nextFilters.selectedChannels || {}
      )
        .filter(([key, value]) => key.startsWith("channel___") && isSelected(value))
        .map(([key]) => key.replace("channel___", ""));
      const selectedIssueKeys = Object.entries(nextFilters.selectedFilters || {})
        .filter(([_, value]) => isSelected(value))
        .map(([key]) => key);

      const query = buildQueryString({
        videoId: nextFilters.selectedVideoID,
        cms: nextFilters.selectedCMS,
        channel: selectedChannelNames,
        channelId: nextFilters.selectedChannelID,
        channelManager: nextFilters.selectedChannelManagers,
        issue: selectedIssueKeys,
        privacyStatus: nextFilters.selectedPrivacyStatus,
        contentFormat: nextFilters.selectedContentFormat,
        legalEntity: nextFilters.selectedLegalEntity,
        legalEntityType: nextFilters.selectedLegalEntityType,
        myIssuesOnly: nextFilters.myIssuesOnly,
      });

      navigate({ search: query }, { replace: true });
    },
    [navigate]
  );

  const handleSearch = useCallback(
    (nextFilters: any) => {
      trackToolAction("YouTubeScorecard", "search");
      handleFilterChange(nextFilters);
      updateUrlFromFilters(nextFilters);
    },
    [handleFilterChange, updateUrlFromFilters]
  );

  const handleReset = useCallback(
    (nextFilters: any) => {
      handleFilterChange(nextFilters);
      navigate({ search: "" }, { replace: true });
    },
    [handleFilterChange, navigate]
  );

  const [statsCards, setStatsCards] = useState<StatisticPanelsProps>({
    cards: [
      {
        header: "Total Issues",
        tooltip: "Total number of videos with metadata discrepancy",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "totalIssuesMetadata",
      },
      {
        header: "Custom ID",
        tooltip: "Videos with incorrect custom ID",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "incorrectCustomID",
      },
      {
        header: "Metadata",
        tooltip: "Videos with missing metadata (Title, description)",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "incorrectMetadata",
      },
      {
        header: "Category",
        tooltip: "Videos with incorrect category",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "incorrectCategory",
      },
      {
        header: "MFK Status",
        tooltip: "Videos with MFK status disabled",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "incorrectMFK",
      },
      {
        header: "Custom Thumbnail",
        tooltip: "Videos without custom thumbnail",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "incorrectThumbnail",
      },
      // {
      //   header: "Upload Language",
      //   tooltip: "Videos with incorrect Language",
      //   value: <Skeleton />,
      //   difference: <Skeleton />,
      //   id: "incorrectLanguage",
      // },
      {
        header: "Age Restriction",
        tooltip: "Videos with age-restrictions present",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "ageRestriction",
      },
      {
        header: "Paid Promotion",
        tooltip: "Videos with incorrect license",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "paidPromo",
      },
      {
        header: "YouTube License",
        tooltip: "Videos with incorrect license",
        value: <Skeleton />,
        difference: <Skeleton />,
        id: "incorrectLicense",
      },
    ],
  });

  // Update stats when rows change
  useEffect(() => {
    if (rows.length === 0) return;

    const incorrectCustomIDRows = rows.filter(
      (row) => getCustomIDStatus(row) === "Incorrect"
    );
    const incorrectThumbnailRows = rows.filter((row) =>
      isMissingCustomThumbnail(row)
    );
    const incorrectMFKRows = rows.filter((row) => row.mfk === "No");
    const incorrectCategoryRows = rows.filter(
      (row) => row.categoryStatus === "Incorrect"
    );
    // const incorrectLanguageRows = rows.filter(
    //   (row) => row.LanguageCode === "Incorrect"
    // );
    const incorrectMetadataRows = rows.filter(
      (row) =>
        row.videoTitle === "Missing" ||
        row.Description === "Missing or under 100 characters"
    );
    const incorrectLicenseRows = rows.filter(
      (row) => row.license === "Creative Common"
    );
    const ageRestrictionRows = rows.filter(
      (row) => row.ageRestriction === "Yes"
    );
    const paidPromoRow = rows.filter((row) => row.promotion === "Yes");

    // Calculate total unique videos with any issues
    const videosWithIssues = rows.filter((row) => {
      return (
        getCustomIDStatus(row) === "Incorrect" ||
        row.videoTitle === "Missing" ||
        row.Description === "Missing or under 100 characters" ||
        row.categoryStatus === "Incorrect" ||
        row.mfk === "No" ||
        isMissingCustomThumbnail(row) ||
        // row.LanguageCode === "Incorrect" ||
        row.ageRestriction === "Yes" ||
        row.promotion === "Yes" ||
        row.license === "Creative Common"
      );
    });

    setStatsCards({
      cards: [
        {
          header: "Total Issues",
          tooltip: "Total number of unique videos with any issues",
          value: rows.length.toString(),
          difference: [],
          id: "totalIssuesMetadata",
        },
        {
          header: "Custom ID",
          tooltip: "Videos with an incorrect or missing custom ID",
          value: incorrectCustomIDRows.length.toString(),
          difference: [],
          id: "incorrectCustomID",
        },
        {
          header: "Metadata",
          tooltip: "Videos that are missing a title or description",
          value: incorrectMetadataRows.length.toString(),
          difference: [],
          id: "incorrectMetadata",
        },
        {
          header: "Category",
          tooltip:
            "Videos that are assigned a YouTube video category that is not on the approved internal list",
          value: incorrectCategoryRows.length.toString(),
          difference: [],
          id: "incorrectCategory",
        },
        {
          header: "MFK Status",
          tooltip:
            "Videos where the MFK status is not aligned with the channels MFK designation",
          value: incorrectMFKRows.length.toString(),
          difference: [],
          id: "incorrectMFK",
        },
        {
          header: "Custom Thumbnail",
          tooltip: "Videos that are missing a custom thumbnail",
          value: incorrectThumbnailRows.length.toString(),
          difference: [],
          id: "incorrectThumbnail",
        },
        // {
        //   header: "Upload Language",
        //   tooltip:
        //     "Videos where the language setting on YouTube doesn't match the language of the custom ID",
        //   value: incorrectLanguageRows.length.toString(),
        //   difference: [],
        //   id: "incorrectLanguage",
        // },
        {
          header: "Age Restriction",
          tooltip: "Videos with age-restriction enabled",
          value: ageRestrictionRows.length.toString(),
          difference: [],
          id: "ageRestriction",
        },
        {
          header: "Paid Promotion",
          tooltip:
            "Videos that have the paid promotion disclosure and have not been whitelisted in this tool",
          value: paidPromoRow.length.toString(),
          difference: [],
          id: "paidPromo",
        },
        {
          header: "YouTube License",
          tooltip:
            "Videos that have the incorrect license type applied (E.g. Creative Commons)",
          value: incorrectLicenseRows.length.toString(),
          difference: [],
          id: "incorrectLicense",
        },
      ],
    });
  }, [rows]);

  const lastUpdated = rows[0]?.lastUpdated;

  // Show loading state
  if (loading) {
    return <LoadingState message="Loading metadata..." />;
  }

  // Show error state if data fetching failed
  if (error) {
    return (
      <ErrorState
        error={error}
        onRetry={refreshData}
        title="Error Loading Metadata"
      />
    );
  }

  return (
    <div style={{ width: "100%", marginTop: "-30px", marginBottom: "-30px" }}>
      <div>
        <StatisticPanels cards={statsCards.cards} />
        <Card
          className="flex-column md:flex-col gap-3"
          style={{
            marginTop: "0.3rem",
            marginBottom: "2rem",
            boxShadow: "0px 4px 11px rgba(0, 0, 0, 0.1)",
            borderRadius: "7px",
          }}
        >
          <AuditFilters
            rows={rows}
            onFilterChange={handleFilterChange}
            onSearch={handleSearch}
            onReset={handleReset}
            issueSelction={metadataFilterOptions}
            exportColumns={metadataExportRows}
            filteredRows={filteredRows}
            disabled={myIssuesRows.length <= 0}
            exportName={"Metadata & Properties Audit"}
            myIssuesOnly={myIssuesOnly}
            channelManagerOptions={channelManagerOptionsForFilter}
            initialVideoId={initialFilters.videoId}
            initialCMS={initialFilters.cms}
            initialChannelNames={initialFilters.channel}
            initialChannelId={initialFilters.channelId}
            initialChannelManagers={initialFilters.channelManager}
            initialIssueKeys={initialFilters.issue}
            initialPrivacyStatus={initialFilters.privacyStatus}
            initialContentFormat={initialFilters.contentFormat}
            initialLegalEntity={initialFilters.legalEntity}
            initialLegalEntityType={initialFilters.legalEntityType}
            initialMyIssuesOnly={initialFilters.myIssuesOnly}
          />
          <MetadataTable
            rows={filteredRows}
            loading={loading}
            getCustomIDStatus={getCustomIDStatus}
            getPrivacy={getPrivacy}
            lastUpdated={lastUpdated}
          />
        </Card>
      </div>
    </div>
  );
};

export default MetadataTab;
